import { useState, useEffect } from "react";
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from "@/integrations/supabase/client";

// Shared cache: originalUrl → { blobUrl, refCount }
// Blob URLs are kept alive as long as at least one component references them.
// After all consumers unmount, the entry stays cached for EVICT_DELAY_MS so
// navigating back doesn't re-fetch. After that grace period it's revoked.
const EVICT_DELAY_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  blobUrl: string;
  refCount: number;
  evictTimer: ReturnType<typeof setTimeout> | null;
}

const cache = new Map<string, CacheEntry>();

// In-flight fetch deduplication: if two components mount with the same URL
// at the same time, only one fetch fires.
const pending = new Map<string, Promise<string | null>>();

async function fetchProxyImage(originalUrl: string): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return null;

  const proxyUrl = `${SUPABASE_URL}/functions/v1/proxy-instagram-image?url=${encodeURIComponent(originalUrl)}`;

  const response = await fetch(proxyUrl, {
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: SUPABASE_ANON_KEY,
    },
  });

  if (!response.ok) return null;
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

function acquireCachedImage(originalUrl: string): Promise<string | null> {
  // Already cached — reuse
  const existing = cache.get(originalUrl);
  if (existing) {
    existing.refCount++;
    if (existing.evictTimer) {
      clearTimeout(existing.evictTimer);
      existing.evictTimer = null;
    }
    return Promise.resolve(existing.blobUrl);
  }

  // Already in-flight — piggyback
  const inflight = pending.get(originalUrl);
  if (inflight) {
    return inflight.then((blobUrl) => {
      if (blobUrl) {
        const entry = cache.get(originalUrl);
        if (entry) entry.refCount++;
      }
      return blobUrl;
    });
  }

  // New fetch
  const promise = fetchProxyImage(originalUrl)
    .then((blobUrl) => {
      pending.delete(originalUrl);
      if (blobUrl) {
        cache.set(originalUrl, { blobUrl, refCount: 1, evictTimer: null });
      }
      return blobUrl;
    })
    .catch(() => {
      pending.delete(originalUrl);
      return null;
    });

  pending.set(originalUrl, promise);
  return promise;
}

function releaseCachedImage(originalUrl: string) {
  const entry = cache.get(originalUrl);
  if (!entry) return;

  entry.refCount--;
  if (entry.refCount <= 0) {
    // Grace period before revoking — allows re-navigation without re-fetch
    entry.evictTimer = setTimeout(() => {
      URL.revokeObjectURL(entry.blobUrl);
      cache.delete(originalUrl);
    }, EVICT_DELAY_MS);
  }
}

/**
 * Fetches an Instagram image through the authenticated proxy and returns a blob URL.
 * Results are cached and shared across components — navigating away and back
 * reuses the existing blob URL without re-fetching.
 * Returns null while loading or if the fetch fails.
 */
export const useAuthImage = (originalUrl: string | null | undefined): string | null => {
  const [blobUrl, setBlobUrl] = useState<string | null>(() => {
    // Synchronous cache hit — avoids flash on re-mount
    if (originalUrl) {
      const entry = cache.get(originalUrl);
      if (entry) return entry.blobUrl;
    }
    return null;
  });

  useEffect(() => {
    if (!originalUrl || originalUrl.trim() === "") {
      setBlobUrl(null);
      return;
    }

    // Synchronous cache hit (handles case where initializer didn't match)
    const existing = cache.get(originalUrl);
    if (existing) {
      existing.refCount++;
      if (existing.evictTimer) {
        clearTimeout(existing.evictTimer);
        existing.evictTimer = null;
      }
      setBlobUrl(existing.blobUrl);
      return () => releaseCachedImage(originalUrl);
    }

    setBlobUrl(null);
    let cancelled = false;

    acquireCachedImage(originalUrl).then((url) => {
      if (!cancelled && url) {
        setBlobUrl(url);
      }
    });

    return () => {
      cancelled = true;
      releaseCachedImage(originalUrl);
    };
  }, [originalUrl]);

  return blobUrl;
};

/**
 * Imperative helper: fetches an Instagram image through the authenticated proxy
 * and returns a blob URL. Caller is responsible for revoking the URL when done.
 */
export const preloadAuthImage = async (originalUrl: string): Promise<string | null> => {
  try {
    return await fetchProxyImage(originalUrl);
  } catch {
    return null;
  }
};
